import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { cloneElement, isValidElement } from 'react'
import { boundsFor } from '../../layout/segments'

export interface TooltipProps {
  /** Testo del tooltip (di solito una riga; per HelpTip anche una spiegazione breve). */
  label: ReactNode
  placement?: 'top' | 'bottom' | undefined
  /**
   * Collega il tooltip all'elemento con aria-describedby (default true). Si spegne quando il testo
   * coincide con il nome accessibile (IconButton), per non farlo leggere due volte.
   */
  describe?: boolean | undefined
  /** Il clic (anche col dito) apre e chiude il tooltip: per HelpTip, dove il tooltip è il contenuto. */
  toggleOnClick?: boolean | undefined
  children: ReactElement
}

/** Ritardo prima della comparsa con il puntatore [M3 plain tooltip: 500 ms]. */
const SHOW_DELAY = 500
/** Dopo l'uscita dal bersaglio il tooltip resta 1,5 s [S tooltips: transient]. Il puntatore puo' entrarci (WCAG 1.4.13). */
const HIDE_DELAY = 1500
const GAP = 8
const EDGE = 8

/** Fino a quando (performance.now) i tooltip ignorano il focus: serve ai focus programmatici all'apertura di un overlay. */
let suppressUntil = 0
/**
 * Da chiamare quando un overlay (dialog, menu, modal rail) sposta il focus da codice: per un breve intervallo il
 * focus non apre i tooltip (restano hover e focus da tastiera successivi).
 */
export function suppressTooltipsBriefly(ms = 400): void {
  suppressUntil = performance.now() + ms
}

/** Un solo tooltip alla volta [S]: quello nuovo chiude subito il precedente. */
let activeClose: (() => void) | null = null

type PopoverEl = HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void }

/**
 * Tooltip plain M3 (inverse-surface, body-small, raggio 4). Avvolge un elemento focalizzabile.
 * Compare al passaggio del mouse (dopo 500 ms) e al focus da tastiera (subito); Esc lo chiude.
 * Il wrapper e' `display: contents`: non cambia il layout di chi lo contiene.
 */
export function Tooltip({ label, placement = 'top', describe = true, toggleOnClick, children }: TooltipProps) {
  const id = useId()
  const wrapRef = useRef<HTMLSpanElement | null>(null)
  const tipRef = useRef<PopoverEl | null>(null)
  const showTimer = useRef<number | undefined>(undefined)
  const hideTimer = useRef<number | undefined>(undefined)
  const [visible, setVisible] = useState(false)
  const openAtPress = useRef(false)
  const [pos, setPos] = useState<{ left: number; top: number; place: 'top' | 'bottom' } | null>(null)

  const clearTimers = () => {
    window.clearTimeout(showTimer.current)
    window.clearTimeout(hideTimer.current)
  }

  const close = useCallback(() => {
    window.clearTimeout(showTimer.current)
    window.clearTimeout(hideTimer.current)
    setVisible(false)
    if (activeClose === close) activeClose = null
  }, [])

  const open = useCallback(() => {
    window.clearTimeout(showTimer.current)
    window.clearTimeout(hideTimer.current)
    if (activeClose && activeClose !== close) activeClose()
    activeClose = close
    setVisible(true)
  }, [close])

  const scheduleOpen = (delay: number) => {
    clearTimers()
    if (delay <= 0) open()
    else showTimer.current = window.setTimeout(open, delay)
  }
  const scheduleClose = (delay: number) => {
    window.clearTimeout(showTimer.current)
    window.clearTimeout(hideTimer.current)
    if (delay <= 0) close()
    else hideTimer.current = window.setTimeout(close, delay)
  }

  useEffect(() => () => {
    clearTimers()
    if (activeClose === close) activeClose = null
  }, [close])

  // Posizione: misurata dopo il rendering, sul bersaglio (primo elemento figlio del wrapper).
  useLayoutEffect(() => {
    if (!visible) {
      setPos(null)
      return
    }
    const target = wrapRef.current?.firstElementChild as HTMLElement | null
    const tip = tipRef.current
    if (!target || !tip) return
    try { tip.showPopover?.() } catch { /* senza supporto resta position: fixed */ }
    const r = target.getBoundingClientRect()
    const t = tip.getBoundingClientRect()
    // Il tooltip resta nel segmento dell'ancora.
    const bd = boundsFor(r)
    let place = placement
    if (place === 'top' && r.top - t.height - GAP < bd.top + EDGE) place = 'bottom'
    else if (place === 'bottom' && r.bottom + t.height + GAP > bd.bottom - EDGE) place = 'top'
    const top = place === 'top' ? r.top - t.height - GAP : r.bottom + GAP
    const left = Math.min(Math.max(bd.left + EDGE, r.left + r.width / 2 - t.width / 2), Math.max(bd.left + EDGE, bd.right - t.width - EDGE))
    setPos({ left, top, place })
  }, [visible, placement, label])

  // Esc chiude (WCAG 1.4.13); lo scroll sposterebbe il bersaglio da sotto il tooltip.
  useEffect(() => {
    if (!visible) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Il primo Esc chiude solo il tooltip, non il dialog o il menu che lo contiene.
      e.preventDefault()
      e.stopPropagation()
      close()
    }
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [visible, close])

  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>, describe && visible ? { 'aria-describedby': id } : {})
    : children

  const node: ReactNode = visible ? (
    <div
      ref={tipRef}
      id={id}
      role="tooltip"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...({ popover: 'manual' } as any)}
      className="md-tooltip"
      data-placement={pos?.place ?? placement}
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}
      onPointerEnter={() => window.clearTimeout(hideTimer.current)}
      onPointerLeave={() => scheduleClose(HIDE_DELAY)}
    >
      {label}
    </div>
  ) : null

  return (
    <span
      ref={wrapRef}
      className="md-tooltip-anchor"
      onPointerEnter={(e) => { if (e.pointerType !== 'touch') scheduleOpen(SHOW_DELAY) }}
      onPointerLeave={() => scheduleClose(HIDE_DELAY)}
      onPointerDown={() => { openAtPress.current = visible; if (!toggleOnClick) scheduleClose(0) }}
      onClick={toggleOnClick ? () => { if (openAtPress.current) close(); else open() } : undefined}
      onFocus={(e) => { if (performance.now() >= suppressUntil && (e.target as HTMLElement).matches(':focus-visible')) scheduleOpen(0) }}
      onBlur={() => scheduleClose(0)}
    >
      {child}
      {node}
    </span>
  )
}
